import React, { useEffect, useState } from "react";
import { decryptData } from "../../utils/crypto";
import { notify } from "../../components/Common/Alerts";
import Utils from "../../utils";
import { useConfig } from "../../components/Context/ConfigContext";

const AboutUsPage = () => {
  const customerPoints = [
    "Discover nearby stores using real-time location",
    "Browse products category-wise",
    "Place orders without upfront payment",
    "Choose between pickup or delivery",
    "Enjoy real-time order updates",
  ];

  const storePoints = [
    "Digitize and manage multiple stores easily",
    "Add products with proper pricing and categories",
    "Receive and manage orders efficiently",
    "Track revenue, payments, and daily collections",
    "Grow business with local visibility",
  ];

  const features = [
    "No Advance Payment Model",
    "Strong Focus on Local Businesses",
    "Simple & User-Friendly Platform",
    "Real-Time Updates & Transparency",
    "Flexible Shopping Experience",
  ];

  const { configData } = useConfig();

 
  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-slate-950 px-4 py-8">
      <div className="max-w-5xl mx-auto space-y-10">
        {/* HEADER */}
        <div className="text-center space-y-3">
          <h1 className="text-3xl md:text-4xl font-black text-zinc-900 dark:text-white">
            🏢 About Dukansetu
          </h1>
          <p className="text-sm md:text-base text-zinc-600 dark:text-slate-400 max-w-2xl mx-auto">
            A smart digital platform connecting customers with nearby local
            stores — making shopping simple, fast, and flexible.
          </p>
        </div>

        {/* INTRO */}
        <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 md:p-8 shadow-md border border-zinc-200 dark:border-slate-800">
          <h2 className="text-lg font-black mb-3 text-zinc-800 dark:text-white">
            Welcome to Dukansetu
          </h2>
          <p className="text-sm md:text-base text-zinc-600 dark:text-slate-300 leading-relaxed">
            Dukansetu is a smart digital platform built to connect customers
            with nearby local stores, making shopping faster, easier, and more
            convenient — without the need for advance payments.
          </p>
          <p className="mt-4 text-sm md:text-base text-zinc-600 dark:text-slate-300 leading-relaxed">
            We aim to bridge the gap between local businesses and modern
            consumers by providing a seamless system where customers can explore
            products, place orders, and pay directly at the store.
          </p>
        </div>

        {/* MISSION */}
        <div className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-3xl p-6 md:p-8 shadow-xl">
          <h2 className="text-xl font-black mb-3">🚀 Our Mission</h2>
          <p className="text-sm md:text-base opacity-90 leading-relaxed">
            To empower local stores by digitizing their business and provide
            customers with a smooth, flexible, and reliable shopping experience.
          </p>
          <p className="mt-3 text-sm opacity-90">
            We believe every small and medium business should have access to
            digital tools without complexity.
          </p>
        </div>

        {/* WHAT WE DO */}
        <div>
          <h2 className="text-xl font-black text-blue-600 mb-6">
            🌍 What We Do
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Customers */}
            <div className="bg-white dark:bg-slate-900 border border-zinc-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm">
              <h3 className="font-black text-lg mb-4">👤 For Customers</h3>
              <ul className="space-y-2 text-sm text-zinc-600 dark:text-slate-300">
                {customerPoints.map((item, i) => (
                  <li key={i}>✔ {item}</li>
                ))}
              </ul>
            </div>

            {/* Store Owners */}
            <div className="bg-white dark:bg-slate-900 border border-zinc-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm">
              <h3 className="font-black text-lg mb-4">🏪 For Store Owners</h3>
              <ul className="space-y-2 text-sm text-zinc-600 dark:text-slate-300">
                {storePoints.map((item, i) => (
                  <li key={i}>✔ {item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* WHY */}
        <div>
          <h2 className="text-xl font-black text-green-600 mb-6">
            💡 Why Dukansetu?
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {features.map((feature, i) => (
              <div
                key={i}
                className="bg-white dark:bg-slate-900 border border-zinc-200 dark:border-slate-800 rounded-2xl p-5 text-sm font-semibold text-zinc-700 dark:text-slate-300 shadow-sm hover:shadow-md transition"
              >
                ✔ {feature}
              </div>
            ))}
          </div>

          <p className="mt-6 text-sm md:text-base text-zinc-600 dark:text-slate-300">
            Dukansetu is not just a platform — it's a bridge between trust and
            convenience.
          </p>
        </div>

        {/* RESPONSIBILITY */}
        <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 md:p-8 shadow-md border border-zinc-200 dark:border-slate-800">
          <h2 className="text-xl font-black mb-4 text-zinc-800 dark:text-white">
            🔒 Our Responsibility
          </h2>

          <ul className="space-y-2 text-sm text-zinc-600 dark:text-slate-300">
            <li>✔ Maintaining platform reliability</li>
            <li>✔ Protecting user data and privacy</li>
            <li>✔ Continuously improving user experience</li>
            <li>✔ Supporting local businesses in digital growth</li>
          </ul>
        </div>

        {/* VISION */}
        <div className="bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-3xl p-6 md:p-8 shadow-xl">
          <h2 className="text-xl font-black mb-3">📈 Our Vision</h2>
          <p className="text-sm md:text-base opacity-90 leading-relaxed">
            We envision a future where every local store is digitally connected,
            and every customer can shop nearby with ease, trust, and
            flexibility.
          </p>
        </div>

        {/* CONTACT */}
        <div className="mt-10 text-center">
          <p className="text-sm text-zinc-500 dark:text-slate-400">
            For any queries, contact us at{" "}
            <a
              href={`mailto:${configData?.support_email}`}
              className="text-orange-500 font-semibold hover:underline"
            >
              {configData?.support_email}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AboutUsPage;
